/**
 * The text a card press shows in the OWNER's bubble instead of its id prompt ("Mark booking Dana Levi as paid"),
 * riding as `references.displayText` beside the prompt the agent reads. The harness drops it before the model
 * reads the row, which is why it may carry a client's name when the prompt may only carry ids.
 *
 * Built here, by the app, from the very message it sends ({@link ownerDisplayTextFor}), never taken from the frame: a
 * widget writes a button's label and its prompt both, so a text it wrote could show the owner one thing while the
 * agent is asked another. Only ids are swapped for the names the press sent; every other word is the agent's.
 *
 * Drawn as plain text, cleaned by the rule the harness applies and iOS `OwnerDisplayText` and Android
 * `ownerDisplayText` mirror: control and bidi characters removed, one line, at most {@link MAX_LENGTH} characters,
 * nothing when that leaves it empty.
 *
 * No runtime imports, so the messages API, `use-chat` and a unit test can all read it.
 */

/** Sized for the card prompts themselves, which the text is with one id swapped: they run up to about 620 characters. */
const MAX_LENGTH = 1000;
// Every control character except the whitespace ones, which the collapse below turns into a space, plus the
// bidi marks and overrides that could make the shown name read differently from the stored one.
const STRIPPED = /[\u0000-\u0008\u000E-\u001F\u007F-\u009F\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
/** An id long enough that the owner cannot read it; a short one, like an order number, stays as it is. */
const NAMED_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{7,127}$/;
const TOKEN = /[A-Za-z0-9_-]+/g;
/**
 * A short name: at most three words of letters (any script), joined inside a word only by an apostrophe or a hyphen,
 * and at most {@link NAME_MAX_LENGTH} characters. It can say who or what an id is ("Anne-Marie Cohen", "Linen Apron")
 * and nothing more: no digits, punctuation or sentence a frame could use to add words the agent is not asked.
 */
const SHORT_NAME = /^[\p{L}\p{M}]+(?:['\u2019-][\p{L}\p{M}]+)*(?: [\p{L}\p{M}]+(?:['\u2019-][\p{L}\p{M}]+)*){0,2}$/u;
const NAME_MAX_LENGTH = 40;

function oneLine(value: string): string {
  return value.replace(STRIPPED, '').replace(/\s+/g, ' ').trim();
}

export function ownerDisplayText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const line = oneLine(value);
  if (!line) return undefined;
  const characters = Array.from(line);
  return characters.length > MAX_LENGTH ? `${characters.slice(0, MAX_LENGTH - 1).join('').trimEnd()}…` : line;
}

/** The names a card press sent for the ids in its prompt (dashboard `runtime.js` promptNames), by id. */
export type RecordNames = ReadonlyMap<string, string>;

/**
 * The frame's `names`: exactly one long id and its short name ({@link SHORT_NAME}), or nothing. One, because a row has
 * one person or item: a second id in a prompt (a product's variant) is a different record that name would mislabel.
 */
export function recordNames(value: unknown): RecordNames | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const entries = Object.entries(value);
  if (entries.length !== 1) return undefined;
  const names = new Map<string, string>();
  for (const [id, name] of entries) {
    const line = typeof name === 'string' ? oneLine(name) : '';
    if (!NAMED_ID.test(id) || !/[0-9]/.test(id) || Array.from(line).length > NAME_MAX_LENGTH || !SHORT_NAME.test(line)) return undefined;
    names.set(id, line);
  }
  return names;
}

/**
 * What the owner's bubble shows for `message`, the text the agent is sent: that text with each whole id token `names`
 * covers swapped for its name, as one line. Nothing when no id was swapped, when the message carries any other long
 * id (a second record the name would mislabel), or when the line is longer than {@link MAX_LENGTH} characters: cutting
 * it would hide words the agent reads, so the bubble shows the message itself.
 */
export function ownerDisplayTextFor(message: string, names: RecordNames | undefined): string | undefined {
  if (!names) return undefined;
  const longIds = new Set((message.match(TOKEN) ?? []).filter((token) => NAMED_ID.test(token) && /[0-9]/.test(token)));
  if (longIds.size !== 1) return undefined;
  let swapped = false;
  const line = oneLine(
    message.replace(TOKEN, (token) => {
      const name = names.get(token);
      if (name === undefined) return token;
      swapped = true;
      return name;
    }),
  );
  return swapped && line && Array.from(line).length <= MAX_LENGTH ? line : undefined;
}

/** The owner's text a stored USER row was sent with (`metadata.references.displayText`). */
export function ownerDisplayTextOf(metadata: unknown): string | undefined {
  const references = objectOf(objectOf(metadata)?.references);
  return ownerDisplayText(references?.displayText);
}

/** An object, or the JSON string Wix `metadata` maps often hold one as, at either layer. */
function objectOf(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}
